import type {ReactElement, SyntheticEvent} from 'react'
import StorageHelper from '@freckle/educator-entities/ts/common/helpers/storage-helper'
import type {CourseAttrs} from '@freckle/educator-entities/ts/roster/models/course'
import type {CourseMembershipAttrs} from '@freckle/educator-entities/ts/roster/models/course-membership'
import type {StudentAttrs} from '@freckle/educator-entities/ts/users/models/student'
import {OverlayTrigger} from '@freckle/educator-materials/ts/components/overlay-trigger'
import {Popover} from '@freckle/educator-materials/ts/components/popover'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {SafeTrans} from '@freckle/educator-materials/ts/helpers/translate/component'
import {getStudentTeacherLoginUrl} from '@freckle/classroom/ts/common/logic/sign-in-as-student'
import {ExternalLinkWithConfirmation} from './../external-link-with-confirmation'

const HELP_LINK = 'https://help2.renaissance.com/freckle/115002785093'

function navigateTo(mLoginUrl: string | undefined) {
  if (mLoginUrl !== undefined) {
    // @ts-ignore https://app.asana.com/0/1207361940282681/1208561050845457/f
    window.location = mLoginUrl
  }
}

type Props = {
  courses: Array<CourseAttrs>
  students: Array<StudentAttrs>
  courseMemberships: Array<CourseMembershipAttrs>
  render: (onClickViewStudent: (e: SyntheticEvent<EventTarget, Event>) => void) => ReactElement
}

export function ViewStudentAppButton(props: Props) {
  const {t} = useSafeTranslation()
  const {courses, students, courseMemberships, render} = props
  const mLoginUrl = getStudentTeacherLoginUrl(courses, students, courseMemberships)

  function onClickViewStudent(e: SyntheticEvent<EventTarget, Event>) {
    if (mLoginUrl !== undefined) {
      // only stopPropagation when we have a login url
      // otherwise the popver (which only render when we dont have a login url)
      // will not be triggered

      StorageHelper.setLocalStorageProperty('student-app-viewed', true)
      navigateTo(mLoginUrl)

      e.stopPropagation()
    }
  }

  const signInOverlay = (
    <Popover id="cannot-sign-in-popover" title={t('VIEW_STUDENT_APP_CANT_SIGN_TITLE')}>
      <SafeTrans
        i18nKey="VIEW_STUDENT_APP_CANT_SIGNIN_CONTENT"
        components={{
          studentAppLink: <ExternalLinkWithConfirmation href="https://student.freckle.com" />,
          helpLink: <ExternalLinkWithConfirmation href={HELP_LINK} />
        }}
      />
    </Popover>
  )

  const renderedButton = render(onClickViewStudent)

  const trigger = 'click' // anything else will BREAK the model that opens from inside the popover
  const linkWithOverlay =
    mLoginUrl === undefined ? (
      <OverlayTrigger trigger={trigger} placement="bottom" overlay={signInOverlay}>
        {/*
            stopPropagation when popover is shown this is
            helpful in case the popover is displayed in a component
            that interacts on click like an expandableContainer
          */}
        <span onClick={e => e.stopPropagation()}>{renderedButton}</span>
      </OverlayTrigger>
    ) : (
      renderedButton
    )

  return linkWithOverlay
}
